import React, { useContext } from 'react';

import styled from '@emotion/styled';

import useTopTier from '../../../../hooks/dashboard/performance-monitoring/useTopTier';
import {
  TopTierDataType,
  TopTierInitialValues,
  TopTierSchema,
} from '../../../../models/dashboard/performance-monitoring/top-tier';
import { useAppSelector } from '../../../../redux/store';
import { FormSubmitButton } from '../../../Button/FormSubmitButton';
import AutoSetDependantInputField from '../../../FormCom/AutoSetDependantInputField';
import { FormComponent } from '../../../FormCom/FormComponent';
import { PercentageOnlyInputField } from '../../../FormCom/PercentageOnlyInputField';
import Tooltip from '../../../Tooltip/Tooltip';
import ModalContainer from '../../ModalContainer';
import { ModalContext } from '../../ModalContext';
import PlainSuccessModal from '../../PlainSuccessModal';

const TopTierBenchmarkModal = () => {
  const modalContext = useContext(ModalContext);
  const { setTopTier, loading } = useTopTier();
  const { currentConfigData } = useAppSelector(
    (state) => state.currentConfigData,
  );

  const successActionFn = () => {
    modalContext?.setShowModal(false);
  };

  const onSubmit = (value: TopTierDataType) => {
    setTopTier(value, () => {
      modalContext?.setShowSuccessComponent(true);
      modalContext?.setSuccessDescription(
        `Company performance tiers have been configured successfully`,
      );
    });
  };

  if (
    currentConfigData?.tierThreshold &&
    Object.keys(currentConfigData.tierThreshold).length > 0
  ) {
    TopTierInitialValues.platinum = String(
      currentConfigData.tierThreshold.Platinum,
    );
    TopTierInitialValues.gold = String(currentConfigData.tierThreshold.Gold);
    TopTierInitialValues.silver = String(
      currentConfigData.tierThreshold.Silver,
    );
    TopTierInitialValues.risk = String(currentConfigData.tierThreshold.Risk);
  }

  return (
    <ModalContainer
      label='Company Performance Tier Setup'
      description='Set minimum percentile thresholds for each performance tier.'
      successComponent={() => (
        <PlainSuccessModal
          successActionFn={successActionFn}
          successLabel='Benchmark updated successfully'
          description={modalContext?.successDescription ?? ''}
          btnLabel='Close'
          // className='pink'
        />
      )}
      showSuccessComponent={modalContext?.showSuccessComponent}
    >
      <FormComponent
        initialValues={TopTierInitialValues}
        schema={TopTierSchema}
        onSubmit={onSubmit}
        className={'large-gap'}
      >
        <Header>
          <Label>Tier Name</Label>
          <Label className='right-align'>Minimum Percentage</Label>
        </Header>

        <FormRow>
          <DecoratedLabel>
            <span className='pallette platinum'></span>
            Platinum{' '}
            <Tooltip content='Top-tier companies demonstrating outstanding performance, trust, and operational excellence.' />
          </DecoratedLabel>

          <PercentageOnlyInputField
            label=''
            name='platinum'
            id='platinum'
            type='text'
            placeholder='0'
          />
        </FormRow>

        <FormRow>
          <DecoratedLabel>
            <span className='pallette gold'></span>
            Gold
            <Tooltip content='High-performing companies delivering reliable service and strong operational consistency.' />
          </DecoratedLabel>

          <PercentageOnlyInputField
            label=''
            name='gold'
            id='gold'
            type='text'
            placeholder='0'
          />
        </FormRow>

        <FormRow>
          <DecoratedLabel>
            <span className='pallette silver'></span>
            Silver
            <Tooltip content='Companies meeting basic performance expectations with stable and acceptable service levels.' />
          </DecoratedLabel>

          <PercentageOnlyInputField
            label=''
            name='silver'
            id='silver'
            type='text'
            placeholder='0'
          />
        </FormRow>

        <AutoSetDependantInputField
          controlKey='silver'
          dependantKey='risk'
          dependantValueComputeFn={(silver) =>
            silver ? `0 - ${Number(silver) - 1}` : ''
          }
        />

        <FormRow>
          <DecoratedLabel>
            <span className='pallette at-risk'></span>
            At Risk
            <Tooltip content='Companies showing performance issues and requiring improvement to remain in good standing.' />
          </DecoratedLabel>

          <PercentageOnlyInputField
            label=''
            disabled
            allowHype
            name='risk'
            id='risk'
            type='text'
            placeholder='0'
          />
        </FormRow>

        <ButtonArea>
          <FormSubmitButton
            className='clear fit'
            onClick={() => modalContext?.setShowModal(false)}
            btnType='button'
          >
            Cancel
          </FormSubmitButton>
          <FormSubmitButton loading={loading} className='fit'>
            Save Benchmark
          </FormSubmitButton>
        </ButtonArea>
      </FormComponent>
    </ModalContainer>
  );
};

export default TopTierBenchmarkModal;

const ButtonArea = styled.div`
  display: flex;
  /* grid-template-columns: 1fr 1fr; */
  gap: 12px;
  margin-top: 32px;
  margin-left: auto;
`;

const Header = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
`;

const Label = styled.div`
  font-size: 14px;
  line-height: 14px;
  font-weight: 500;
  color: #5c5963;
  padding: 16px;
  border-bottom: 1px solid ${({ theme }) => theme.palette.borderColor};
  margin-bottom: -24px;

  &.right-align {
    text-align: right;
  }
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  align-items: center;
  gap: 12px;
  padding-left: 16px;
  padding-right: 16px;
  /* border: 1px solid red; */
  margin-bottom: -12px;
`;

const DecoratedLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  line-height: 14px;
  font-weight: 400;
  color: #6e7079;

  & > span.pallette {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background-color: #4caf50;

    &.platinum {
      background-color: #5b91f5;
    }

    &.gold {
      background-color: #ffc400;
    }
    &.silver {
      background-color: #cccccc;
    }
    &.at-risk {
      background-color: #ff0500;
    }
  }
`;
